/**
 * Workflow API client. All writes to workflow templates / runs / tasks go
 * through the server engine (Firestore rules block browser writes); reads of
 * runs and tasks stay live via workflowFirestoreService.js.
 */
import { auth } from '../firebase';
import { env } from '../config/environment';
import { toBrisbaneWall } from '../utils/calendarTimeZone';

async function authHeaders() {
  const idToken = await auth.currentUser?.getIdToken();
  if (!idToken) throw new Error('Not authenticated');
  return {
    Authorization: `Bearer ${idToken}`,
    'Content-Type': 'application/json',
  };
}

async function readJson(response) {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data.error || `Request failed (${response.status}) — is the backend up to date?`);
    error.status = response.status;
    error.data = data;
    if (data.validation) error.validation = data.validation;
    throw error;
  }
  return data;
}

async function request(url, { method = 'GET', body } = {}) {
  const headers = await authHeaders();
  const response = await fetch(url, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  return readJson(response);
}

// ── Admin: templates ───────────────────────────────────────────────────────

/** @param {string|null} teamId  advisory team id, '__global__', or null/'all' for every scope */
export async function listWorkflowTemplates({ teamId = null, includeArchived = false } = {}) {
  const params = new URLSearchParams();
  if (teamId) params.set('teamId', teamId);
  if (includeArchived) params.set('includeArchived', 'true');
  const query = params.toString();
  const data = await request(`${env.apiEndpoints.adminWorkflowTemplates}${query ? `?${query}` : ''}`);
  return data.templates || [];
}

export async function fetchWorkflowTemplateRunCounts() {
  const data = await request(env.apiEndpoints.adminWorkflowTemplateRunCounts);
  return data.counts || {};
}

/**
 * Saved workflow-map layout for a firm: block positions per view
 * ({ workflows: { [templateId]: { x, y } }, tasks: {...} }) plus per-workflow
 * colour overrides ({ [templateId]: '#rrggbb' }).
 */
export async function fetchWorkflowMapLayout(teamId) {
  const data = await request(`${env.apiEndpoints.adminWorkflowMapLayout}?teamId=${encodeURIComponent(teamId)}`);
  return {
    positions: { workflows: data.positions?.workflows || {}, tasks: data.positions?.tasks || {} },
    colours: data.colours || {},
  };
}

/** Replace the firm's per-workflow map colour overrides (omit a template to return it to the automatic colour). */
export async function saveWorkflowMapColours(teamId, colours) {
  const data = await request(env.apiEndpoints.adminWorkflowMapColours, { method: 'PUT', body: { teamId, colours: colours || {} } });
  return data.colours || {};
}

/** The firm's workflow roles: [{ id, name, email, displayName }]. */
export async function fetchWorkflowFirmRoles(teamId) {
  const data = await request(`${env.apiEndpoints.adminWorkflowFirmRoles}?teamId=${encodeURIComponent(teamId)}`);
  return { teamId: data.teamId || teamId, roles: data.roles || [], updatedAt: data.updatedAt || null, updatedBy: data.updatedBy || null };
}

/**
 * Replace the firm's workflow roles. Holder changes re-point open tasks server-side
 * (`repointed.tasks`). Removing a referenced role fails with 409 (`err.data.references`)
 * unless `force`.
 */
export async function saveWorkflowFirmRoles(teamId, roles, { force = false } = {}) {
  const data = await request(env.apiEndpoints.adminWorkflowFirmRoles, { method: 'PUT', body: { teamId, roles: roles || [], ...(force ? { force: true } : {}) } });
  return {
    roles: data.roles || [],
    repointed: data.repointed || { tasks: 0, roles: [] },
    repointError: data.repointError || null,
    updatedAt: data.updatedAt || null,
    updatedBy: data.updatedBy || null,
  };
}

/** The firm's meeting-type catalogue (always includes the Welcome call + Annual review system types). */
export async function fetchWorkflowMeetingTypes(teamId) {
  const data = await request(`${env.apiEndpoints.adminWorkflowMeetingTypes}?teamId=${encodeURIComponent(teamId)}`);
  return { teamId: data.teamId || teamId, types: data.types || [], saved: Boolean(data.saved), updatedAt: data.updatedAt || null, updatedBy: data.updatedBy || null };
}

/**
 * Replace the firm's meeting types. Removing a type a template still books fails
 * with 409 (`err.data.references`) unless `force`.
 */
export async function saveWorkflowMeetingTypes(teamId, types, { force = false } = {}) {
  const data = await request(env.apiEndpoints.adminWorkflowMeetingTypes, { method: 'PUT', body: { teamId, types: types || [], ...(force ? { force: true } : {}) } });
  return { types: data.types || [], updatedAt: data.updatedAt || null, updatedBy: data.updatedBy || null };
}

/** Replace the firm's saved block positions (pass {} to reset to the automatic layout). */
export async function saveWorkflowMapLayout(teamId, positions) {
  const data = await request(env.apiEndpoints.adminWorkflowMapLayout, { method: 'PUT', body: { teamId, positions: positions || {} } });
  return { workflows: data.positions?.workflows || {}, tasks: data.positions?.tasks || {} };
}

export async function validateWorkflowTemplateRemote(template) {
  return request(env.apiEndpoints.adminWorkflowTemplateValidate, { method: 'POST', body: template });
}

/** Create (no id) or update (id) a template. Returns the stored template. */
export async function saveWorkflowTemplate(template) {
  const url = template?.id
    ? env.apiEndpoints.adminWorkflowTemplate(template.id)
    : env.apiEndpoints.adminWorkflowTemplates;
  const data = await request(url, { method: template?.id ? 'PUT' : 'POST', body: template });
  return data.template;
}

export async function archiveWorkflowTemplate(templateId, archived = true) {
  const data = await request(env.apiEndpoints.adminWorkflowTemplateArchive(templateId), {
    method: 'POST',
    body: { archived },
  });
  return data.template;
}

/**
 * Copy a template to a firm (the same firm is allowed). Resolves to
 * `{ template, warnings, createdRoles, validation }`; a 400 carries
 * `error.validation` and `error.data.warnings` (what was remapped before it failed).
 */
export async function copyWorkflowTemplate(templateId, { targetTeamId, name }) {
  return request(env.apiEndpoints.adminWorkflowTemplateCopy(templateId), {
    method: 'POST',
    body: { targetTeamId, name },
  });
}

/** Permanently delete an archived template (runs keep their snapshot). */
export async function deleteWorkflowTemplate(templateId) {
  return request(env.apiEndpoints.adminWorkflowTemplateDelete(templateId), { method: 'DELETE' });
}

// ── Client: runs ───────────────────────────────────────────────────────────

/**
 * Non-archived templates of the client's firm, in picker/map shape:
 * { id, name, category, description, version, autoStartOnOnboarding, taskCount, ruleCount, tasks[], links[] }.
 */
export async function listStartableWorkflowTemplates(clientId) {
  const data = await request(env.apiEndpoints.clientWorkflowTemplates(clientId));
  return data.templates || [];
}

/** Read-only twin of fetchWorkflowMapLayout scoped by the client's firm: saved block positions + colour overrides. */
export async function fetchClientWorkflowMapLayout(clientId) {
  const data = await request(env.apiEndpoints.clientWorkflowMapLayout(clientId));
  return {
    teamId: data.teamId || null,
    positions: { workflows: data.positions?.workflows || {}, tasks: data.positions?.tasks || {} },
    colours: data.colours || {},
  };
}

/** The client firm's workflow roles (read-only) for labels and email-recipient resolution. */
export async function fetchClientWorkflowRoles(clientId) {
  const data = await request(env.apiEndpoints.clientWorkflowRoles(clientId));
  return { teamId: data.teamId || null, roles: data.roles || [] };
}

/** The client firm's meeting-type catalogue (read-only) for booking dialogs. */
export async function fetchClientMeetingTypes(clientId) {
  const data = await request(env.apiEndpoints.clientMeetingTypes(clientId));
  return { teamId: data.teamId || null, types: data.types || [] };
}

/**
 * Firm members with a connected Microsoft calendar, plus (with a task id) the
 * task's designated calendar owner: { calendars: [{ email, name, role }], designated: { email, name }|null }.
 */
export async function fetchBookableCalendars(clientId, taskId = null) {
  const data = await request(env.apiEndpoints.clientBookableCalendars(clientId, taskId));
  return { calendars: data.calendars || [], designated: data.designated || null };
}

/**
 * Custom "Book meeting" step: `{ action: 'prompted' | 'book' | 'reschedule' | 'cancel' | 'notNeeded', … }`.
 * book: { calendarOwnerEmail, startDateTime, endDateTime, sendClientInvite }. Times may carry any
 * offset; they are sent as Brisbane wall time, which is what task.booking stores.
 * Response: { booking, clientInvite? }.
 */
export async function postWorkflowTaskBooking(clientId, taskId, body) {
  const payload = { ...body };
  if (payload.startDateTime) payload.startDateTime = toBrisbaneWall(payload.startDateTime);
  if (payload.endDateTime) payload.endDateTime = toBrisbaneWall(payload.endDateTime);
  return request(env.apiEndpoints.clientWorkflowTaskBooking(clientId, taskId), { method: 'POST', body: payload });
}

/** Start an ad hoc workflow (one task named by the user, assigned to them). Same result shape as startWorkflowRun. */
export async function startAdHocWorkflowRun(clientId, name) {
  return request(env.apiEndpoints.clientWorkflowRunAdHoc(clientId), { method: 'POST', body: { name } });
}

/** `label` = round name, required by the server when the template has `promptRunLabel`. */
export async function startWorkflowRun(clientId, templateId, label = null) {
  return request(env.apiEndpoints.clientWorkflowRuns(clientId), { method: 'POST', body: label ? { templateId, label } : { templateId } });
}

/**
 * Onboarding start. `trigger: 'created'` starts the firm's onboarding workflow or
 * marks the client pending; `'firm-assigned'` retries only for pending clients.
 */
export async function autoStartOnboardingWorkflows(clientId, { trigger = 'created' } = {}) {
  return request(env.apiEndpoints.clientWorkflowRunsAutoStart(clientId), { method: 'POST', body: { trigger } });
}

export async function cancelWorkflowRun(clientId, runId) {
  return request(env.apiEndpoints.clientWorkflowRunCancel(clientId, runId), { method: 'POST', body: {} });
}

/** Permanently removes a cancelled run and its tasks (server refuses other statuses). */
export async function deleteWorkflowRun(clientId, runId) {
  return request(env.apiEndpoints.clientWorkflowRun(clientId, runId), { method: 'DELETE' });
}

// ── Client: tasks ──────────────────────────────────────────────────────────

function transition(action) {
  return (clientId, taskId, body = {}) => request(
    env.apiEndpoints.clientWorkflowTaskAction(clientId, taskId, action),
    { method: 'POST', body: body || {} },
  );
}

export const startWorkflowTask = transition('start');
export const completeWorkflowTask = transition('complete');
export const skipWorkflowTask = transition('skip');
export const reopenWorkflowTask = transition('reopen');

/**
 * @param {'start'|'complete'|'skip'|'reopen'|'sendBack'|'unstart'} action
 * @param {object} [body]  milestone payload — see server/lib/workflows/engine.cjs transitionWorkflowTask
 */
export async function transitionWorkflowTask(clientId, taskId, action, body = {}) {
  return request(env.apiEndpoints.clientWorkflowTaskAction(clientId, taskId, action), { method: 'POST', body: body || {} });
}

/** Run every server guard for an action without writing (before any email is sent). */
export async function preflightWorkflowTaskAction(clientId, taskId, action, body = {}) {
  return transitionWorkflowTask(clientId, taskId, action, { ...(body || {}), preflight: true });
}

/**
 * Mark a task done after its milestone action ran in the browser. Idempotent on
 * the server for the same correlationId, so it is safe to retry.
 */
export async function completeWorkflowTaskAsMilestone(clientId, taskId, { milestoneActionId = null, correlationId = null, outcome = null, overrideGates = false, completionMode = null } = {}) {
  return transitionWorkflowTask(clientId, taskId, 'complete', {
    milestoneActionId, correlationId, outcome, ...(overrideGates ? { overrideGates: true } : {}), ...(completionMode ? { completionMode } : {}),
  });
}

/**
 * Send one "on completion" email step server-side (attachments read from
 * storage, .eml proof-of-send stored when the client is a recipient).
 * @returns {Promise<{ fileNoteId, emlDocumentId, to, cc, subject, attachmentCount, audience }>}
 */
/**
 * Marketing Portal "Submit Fact Find": the server runs the open Complete Fact
 * Find task's completion (its own email, with the note as {marketerNote} and
 * the attached client documents on its staff emails).
 * → { results: [{ taskId, status, reason? }], completed }; results is empty
 * when the client has no open Complete Fact Find task.
 */
export async function completeFactFindWorkflowTask(clientId, { noteHtml = '', attachmentDocumentIds = [] } = {}) {
  return request(env.apiEndpoints.clientWorkflowFactFindComplete(clientId), { method: 'POST', body: { noteHtml, attachmentDocumentIds } });
}

/**
 * Marketing Portal "Resubmit": emails "Fact Find Updated" (the marketer's
 * message, a fresh summary) to the done Complete Fact Find task's recipients.
 * Pass presentationDate/Time only when the marketer changed them;
 * attachmentDocumentIds are client documents attached to the email.
 * → { taskId, sent, replyTo, presentationChanged, resubmittedAt }
 */
export async function resubmitFactFindWorkflowTask(clientId, { noteHtml = '', presentationDate = '', presentationTime = '', attachmentDocumentIds = [] } = {}) {
  return request(env.apiEndpoints.clientWorkflowFactFindResubmit(clientId), {
    method: 'POST',
    body: { noteHtml, attachmentDocumentIds, ...(presentationDate || presentationTime ? { presentationDate, presentationTime } : {}) },
  });
}

export async function sendWorkflowCompletionEmail(clientId, taskId, { stepId, correlationId, to, cc, subject = null, body = null, attachmentDocumentIds = [], clientUpdates = null, documentRequest = null }) {
  return request(env.apiEndpoints.clientWorkflowTaskCompletionEmail(clientId, taskId), {
    method: 'POST',
    // clientUpdates: stamps the completion is about to write, merged into the client for rendering only.
    // documentRequest: the New Documents request made in this completion ({documentRequestList}).
    body: { stepId, correlationId, to, cc, subject, body, attachmentDocumentIds, ...(clientUpdates ? { clientUpdates } : {}), ...(documentRequest ? { documentRequest } : {}) },
  });
}

/**
 * Amendments requested: returns the task to Not started and reopens the
 * drafting predecessor(s). Pass `fileNoteId` to link an existing file note, or
 * `note: { title, body }` for the engine to create one, to both tasks.
 */
export async function sendBackWorkflowTask(clientId, taskId, { milestoneActionId = null, correlationId = null, amendment = null, fileNoteId = null, note = null } = {}) {
  return transitionWorkflowTask(clientId, taskId, 'sendBack', { milestoneActionId, correlationId, amendment, fileNoteId, note });
}

/**
 * Run a completion outcome server-side. "No answer": the task returns to Not
 * started, the booking task reopens, the client's booking is cleared, the
 * attempt is counted and the Outlook event cancelled (left in the calendar with
 * `keepBooking`). Response carries `calendar.status`
 * (cancelled | not_connected | not_found | failed | no_event | kept).
 */
export async function runWorkflowTaskOutcome(clientId, taskId, { outcomeId, correlationId = null, keepBooking = false }) {
  return request(env.apiEndpoints.clientWorkflowTaskOutcome(clientId, taskId), { method: 'POST', body: { outcomeId, correlationId, keepBooking: keepBooking === true } });
}

/**
 * "Assign adviser" step: set (or clear, with an empty email) the client's
 * Assigned Adviser. Open tasks assigned through "Assigned Adviser" move to the
 * new adviser. Response: { assignment: { email, name }, previous, changed, repointed: { tasks }, repointError? }.
 * @param {{ email: string }} body
 */
export async function assignWorkflowTaskAdviser(clientId, taskId, { email }) {
  return request(env.apiEndpoints.clientWorkflowTaskAssignAdviser(clientId, taskId), { method: 'POST', body: { email: email || '' } });
}

/**
 * Change the client's one Assigned Adviser (src/utils/currentAdviser.js) — the
 * only path after creation. The server checks the adviser belongs to the
 * client's firm, audits `assigned_adviser` with `source`, and re-points open
 * tasks assigned through "Assigned Adviser". Empty email clears it.
 * @param {{ email: string, source: 'advice-history'|'manual-override'|'bulk-override'|'ar-booking', reason?: string }} body
 * @returns {Promise<{ assignment: {email,name}, previous: {email,name}, changed: boolean, repointed: {tasks:number}, repointError?: string }>}
 */
export async function setClientAdviser(clientId, { email, source, reason = '' }) {
  return request(env.apiEndpoints.clientAdviser(clientId), { method: 'POST', body: { email: email || '', source, reason } });
}

/**
 * @param {object} patch  any of title, assignedTo, assignedToName, startDate,
 *   dueDate, checklist, folderId, folderName, envelopeId,
 *   envelopeIds (string[] — every linked envelope; the engine keeps envelopeId = newest link),
 *   bookingRequest ({ date, time, sentAt, mode } | null — welcome-call special request)
 */
export async function updateWorkflowTask(clientId, taskId, patch) {
  return request(env.apiEndpoints.clientWorkflowTask(clientId, taskId), { method: 'PATCH', body: patch });
}

/**
 * Tell the engine a welcome call was booked or a special request sent, so open
 * Book Welcome Call tasks complete (marketers may call it). Best-effort: never throws.
 * @returns {Promise<{ completed: string[] }>}
 */
export async function notifyWelcomeCallBooked(clientId) {
  if (!clientId) return { completed: [] };
  try {
    return await request(env.apiEndpoints.clientWorkflowWelcomeCallBooked(clientId), { method: 'POST', body: {} });
  } catch (error) {
    console.warn('Welcome call booking could not update the workflow:', error?.message || error);
    return { completed: [] };
  }
}

/**
 * After the welcome call time is set by hand (Manual Override): open Complete
 * Welcome Call tasks move to the call's day. Best-effort: never throws.
 * @returns {Promise<{ updated: string[] }>}
 */
export async function syncWelcomeCallDueDate(clientId) {
  if (!clientId) return { updated: [] };
  try {
    return await request(env.apiEndpoints.clientWorkflowWelcomeCallDueDate(clientId), { method: 'POST', body: {} });
  } catch (error) {
    console.warn('Welcome call due date could not update the workflow:', error?.message || error);
    return { updated: [] };
  }
}

/** @param {{ title, body, kind?: 'completion'|null, category?: string|null }} note */
/** `callNoteType`: the call note template the note was written from ('arReviewCall'). */
export async function addWorkflowTaskNote(clientId, taskId, { title, body, kind = null, category = null, callNoteType = null }) {
  return request(env.apiEndpoints.clientWorkflowTaskNotes(clientId, taskId), {
    method: 'POST',
    body: { title, body, ...(kind ? { kind } : {}), ...(category ? { category } : {}), ...(callNoteType ? { callNoteType } : {}) },
  });
}
